import { env } from "@/redux/http";
import { useState, useEffect, useCallback } from "react";

interface UseDebugGestureOptions {
  targetElement?: string; // CSS selector for the element to tap
  tapCount?: number; // Number of taps required
  tapWindow?: number; // Time window for taps in milliseconds
}

export const useDebugGesture = (options: UseDebugGestureOptions = {}) => {
  const {
    targetElement = "h1", // Default to tapping the h1 (logo/title)
    tapCount = 3,
    tapWindow = 1000,
  } = options;

  const [isDebugOpen, setIsDebugOpen] = useState(false);
  const [tapTimes, setTapTimes] = useState<number[]>([]);

  const handleTap = useCallback(
    (event: Event) => {
      event.preventDefault();

      const now = Date.now();
      const recentTaps = tapTimes.filter((time) => now - time < tapWindow);
      const newTaps = [...recentTaps, now];

      setTapTimes(newTaps);

      if (newTaps.length >= tapCount) {
        setIsDebugOpen(true);
        setTapTimes([]); // Reset taps
      }
    },
    [tapTimes, tapCount, tapWindow]
  );

  useEffect(() => {
    const element = document.querySelector(targetElement);

    if (element) {
      // Use 'click' event which works on both desktop and mobile
      element.addEventListener("click", handleTap);

      // Also add touch event for better mobile responsiveness
      element.addEventListener("touchend", handleTap);

      // Add visual feedback
      if (element instanceof HTMLElement) {
        element.style.cursor = "pointer";
        element.style.userSelect = "none";
      }

      return () => {
        element.removeEventListener("click", handleTap);
        element.removeEventListener("touchend", handleTap);
      };
    }
  }, [targetElement, handleTap]);

  const closeDebug = useCallback(() => {
    setIsDebugOpen(false);
  }, []);

  // Alternative: Keyboard shortcut for development
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Ctrl/Cmd + Shift + D
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "D") {
        e.preventDefault();
        setIsDebugOpen((prev) => !prev);
      }
    };

    if (env === "dev") {
      window.addEventListener("keydown", handleKeyPress);
      return () => window.removeEventListener("keydown", handleKeyPress);
    }
  }, []);

  return {
    isDebugOpen,
    closeDebug,
    openDebug: () => setIsDebugOpen(true),
  };
};
